import type { CustomUser, Team as ApiTeam } from 'api-client/models';
import React, { useEffect, useState } from 'react';
import { Box, Drawer, useTheme, useMediaQuery } from '@mui/material';
import AppNav from './AppNav';
import TopNav from './TopNav';
import ImpersonationWarning from '../../../components/ImpersonationWarning';

const DRAWER_WIDTH = 280;

interface AppLayoutProps {
  user: CustomUser | null;
  team: ApiTeam | null;
  title: string;
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ user, team, title, children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!isMobile) setMobileDrawerOpen(false);
  }, [isMobile]);

  const closeDrawer = (): void => setMobileDrawerOpen(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh', bgcolor: 'background.default' }}>
      <TopNav
        user={user}
        team={team}
        title={title}
        onMenuToggle={user && isMobile ? () => setMobileDrawerOpen((open) => !open) : undefined}
      />

      {user && (
        <Drawer
          variant="temporary"
          open={isMobile && mobileDrawerOpen}
          onClose={closeDrawer}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              marginTop: '56px',
              height: 'calc(100% - 56px)',
            },
          }}
        >
          <AppNav user={user} team={team} variant="sidebar" onNavigate={closeDrawer} />
        </Drawer>
      )}

      <Box sx={{ display: 'flex', flexGrow: 1, mt: '56px', minWidth: 0 }}>
        {user && !isMobile && (
          <Box
            sx={{
              width: DRAWER_WIDTH,
              flexShrink: 0,
              bgcolor: 'background.paper',
              borderRight: 1,
              borderColor: 'divider',
              height: 'calc(100vh - 56px)',
              overflow: 'auto',
              position: 'sticky',
              top: 56,
            }}
          >
            <AppNav user={user} team={team} variant="sidebar" />
          </Box>
        )}

        {/* Keep page state and pending requests mounted when navigation changes at a breakpoint. */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            p: { xs: 2, sm: 3 },
            width: user ? { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` } : '100%',
          }}
        >
          <ImpersonationWarning />
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AppLayout;
